/**
 * Shared "last activity" for the idle sign-out (8 h by default, N16; AUTH-1, AUTH-4).
 *
 * Stored in localStorage so every tab of the app shares one clock: working in one tab keeps the
 * others signed in, and a sign-out in one tab reaches the rest through the `storage` event.
 *
 * For other lanes: call `touchSessionActivity()` from anything that is activity without input —
 * video playback (throttled here, so calling it from `timeupdate` is fine) or a long upload.
 * That is all a player needs to do.
 */
import { safeNextPath } from "@/lib/safe-next-path";

export const LAST_ACTIVITY_KEY = "lms_spa_last_activity";
/** Written when a tab signs out, so the other tabs follow (AUTH-4). */
export const SESSION_SIGNOUT_KEY = "lms_spa_signout";

/** Writes closer together than this are skipped: pointermove and timeupdate fire constantly. */
export const ACTIVITY_WRITE_THROTTLE_MS = 5_000;

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

/** Marks the session as active now (throttled). Safe to call very often. */
export function touchSessionActivity(
  now: number = Date.now(),
  options: { force?: boolean } = {},
): void {
  if (!options.force) {
    const last = readLastActivity();
    if (last !== null && now >= last && now - last < ACTIVITY_WRITE_THROTTLE_MS) return;
  }
  try {
    storage()?.setItem(LAST_ACTIVITY_KEY, String(now));
  } catch {
    // Storage full or blocked: the idle timer then sees no recorded activity.
  }
}

/** Last activity in any tab, or null when none is recorded. */
export function readLastActivity(): number | null {
  let raw: string | null | undefined;
  try {
    raw = storage()?.getItem(LAST_ACTIVITY_KEY);
  } catch {
    return null;
  }
  const value = raw ? Number.parseInt(raw, 10) : Number.NaN;
  return Number.isFinite(value) && value > 0 ? value : null;
}

export function clearSessionActivity(): void {
  try {
    storage()?.removeItem(LAST_ACTIVITY_KEY);
  } catch {
    // ignore
  }
}

export type SignOutReason = "idle" | "logout";

/** Tells the other tabs that this browser signed out. */
export function broadcastSignOut(reason: SignOutReason): void {
  try {
    storage()?.setItem(SESSION_SIGNOUT_KEY, JSON.stringify({ reason, at: Date.now() }));
  } catch {
    // ignore
  }
}

/** How long after an idle sign-out a redirect to the login page still says why. */
const IDLE_REASON_WINDOW_MS = 10_000;

/** True right after an idle sign-out in this browser (any tab) — see `broadcastSignOut`. */
export function recentIdleSignOut(now: number = Date.now()): boolean {
  let raw: string | null | undefined;
  try {
    raw = storage()?.getItem(SESSION_SIGNOUT_KEY);
  } catch {
    return false;
  }
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw) as { reason?: string; at?: number };
    return (
      parsed.reason === "idle" &&
      typeof parsed.at === "number" &&
      now >= parsed.at &&
      now - parsed.at < IDLE_REASON_WINDOW_MS
    );
  } catch {
    return false;
  }
}

/** The persisted auth store's key (stores/authStore.ts); a manual sign-out clears it. */
const AUTH_STORE_KEY = "lms-auth";

function signedOutByAuthStore(newValue: string | null): boolean {
  if (newValue == null) return true;
  try {
    return (
      (JSON.parse(newValue) as { state?: { isAuthenticated?: boolean } }).state?.isAuthenticated ===
      false
    );
  } catch {
    return false;
  }
}

/**
 * Runs `onSignOut` when another tab signs out — through `broadcastSignOut` (which says why) or
 * by clearing the persisted session (any sign-out path, e.g. the header's button or an expired
 * refresh). Returns the unsubscribe function.
 */
export function onOtherTabSignOut(onSignOut: (reason: SignOutReason) => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  const listener = (event: StorageEvent) => {
    if (event.key === AUTH_STORE_KEY) {
      if (signedOutByAuthStore(event.newValue)) onSignOut("logout");
      return;
    }
    if (event.key !== SESSION_SIGNOUT_KEY || !event.newValue) return;
    let reason: SignOutReason = "logout";
    try {
      const parsed = JSON.parse(event.newValue) as { reason?: string };
      if (parsed.reason === "idle") reason = "idle";
    } catch {
      // malformed: treat as a plain sign-out
    }
    onSignOut(reason);
  };
  window.addEventListener("storage", listener);
  return () => window.removeEventListener("storage", listener);
}

/**
 * AUTH-11: the login page for a signed-out visitor, carrying `?next=` (validated by
 * `safeNextPath`) so sign-in returns to the page they were on.
 */
export function signInPath(
  tenantSlug: string,
  currentPath: string,
  reason?: "idle" | "expired",
): string {
  const params = new URLSearchParams();
  if (reason) params.set("reason", reason);
  const next = safeNextPath(currentPath, tenantSlug);
  if (next) params.set("next", next);
  const query = params.toString();
  return `/t/${tenantSlug}/login${query ? `?${query}` : ""}`;
}
